import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import {
  Layout,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Upload,
  message,
  Space,
  Breadcrumb,
  Dropdown,
  Tooltip,
  Spin,
  Alert,
  Card,
  Divider,
  Row,
  Col,
  Typography,
  Empty,
  Checkbox
} from 'antd';
import type { UploadFile } from 'antd/es/upload/interface';
import {
  FolderOutlined,
  FileOutlined,
  UploadOutlined,
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  DownloadOutlined,
  EllipsisOutlined,
  ArrowLeftOutlined,
  HomeOutlined,
  FileImageOutlined,
  FilePdfOutlined,
  FileWordOutlined,
  FileExcelOutlined,
  FilePptOutlined,
  FileTextOutlined,
  FileZipOutlined,
  FileUnknownOutlined,
  DragOutlined,
  CopyOutlined,
  ScissorOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../stores/root-store-context';
import { ProjectFile } from '../../api/ProjectFilesApiClient';
import dayjs from 'dayjs';
import { canAccessProject } from '../../helpers/ProjectAccessHelper';
import { useCurrentProject } from './ProjectContext';

const { Content } = Layout;
const { Title, Text } = Typography;
const { Dragger } = Upload;

const getFileIcon = (fileName: string) => {
  const extension = fileName.split('.').pop()?.toLowerCase() || '';
  
  switch (extension) {
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'gif':
    case 'bmp':
    case 'svg':
      return <FileImageOutlined />;
    case 'pdf':
      return <FilePdfOutlined />;
    case 'doc':
    case 'docx':
      return <FileWordOutlined />;
    case 'xls':
    case 'xlsx':
    case 'csv':
      return <FileExcelOutlined />;
    case 'ppt':
    case 'pptx':
      return <FilePptOutlined />;
    case 'txt':
    case 'rtf':
    case 'md':
      return <FileTextOutlined />;
    case 'zip':
    case 'rar':
    case '7z':
    case 'tar':
    case 'gz':
      return <FileZipOutlined />;
    default:
      return <FileUnknownOutlined />;
  }
};

const getItemName = (item: ProjectFile): string => {
  return item.isFolder ? item.name : item.fileName;
};

const ProjectFiles = observer(({ isShowInternalLoader }: { isShowInternalLoader: boolean }) => {
  const { t } = useTranslation();
  const { project, projectId, isLoading: isProjectLoading } = useCurrentProject();
  const { applicationStore, projectFilesStore } = useStore();

  const [isCreateFolderModalVisible, setIsCreateFolderModalVisible] = useState(false);
  const [isUploadModalVisible, setIsUploadModalVisible] = useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [isRenameModalVisible, setIsRenameModalVisible] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [newItemName, setNewItemName] = useState('');
  const [uploadFileList, setUploadFileList] = useState<UploadFile<any>[]>([]);
  const [selectedItem, setSelectedItem] = useState<ProjectFile | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('list');
  const [draggedOverId, setDraggedOverId] = useState<number | null>(null);

  useEffect(() => {
    if (projectId) {
      projectFilesStore.loadFiles(projectId, null);
    }
    
    return () => {
      projectFilesStore.clearStore();
    };
  }, [projectId, projectFilesStore]);

  const handleFolderClick = (folder: ProjectFile) => {
    if (projectId && folder.isFolder) {
      projectFilesStore.navigateToFolder(projectId, folder);
    }
  };

  const handleBreadcrumbClick = (index: number) => {
    if (projectId) {
      projectFilesStore.navigateToBreadcrumb(projectId, index);
    }
  };

  const handleGoToParent = () => {
    if (projectId) {
      const parentIndex = projectFilesStore.breadcrumbs.length - 2;
      projectFilesStore.navigateToBreadcrumb(projectId, parentIndex);
    }
  };

  const handleCreateFolder = async () => {
    if (!canManageFiles) return;

    if (!newFolderName.trim() || !projectId) {
      message.error(t('PleaseEnterFolderName'));
      return;
    }
    
    try {
      const success = await projectFilesStore.createFolder(projectId, newFolderName);
      
      if (success) {
        message.success(t('FolderCreated'));
        setNewFolderName('');
        setIsCreateFolderModalVisible(false);
      } else {
        message.error(projectFilesStore.error || t('FailedToCreateFolder'));
      }
    } catch (error) {
      message.error(t('FailedToCreateFolder'));
    }
  };

  const handleUpload = async () => {
    if (!canUploadFiles) return;

    if (uploadFileList.length === 0 || !projectId) {
      message.error(t('NoFilesSelected'));
      return;
    }
    
    try {
      let successCount = 0;
      let failCount = 0;
      
      for (const fileItem of uploadFileList) {
        const file = fileItem.originFileObj;
        
        if (!file) {
          failCount++;
          continue;
        }
        
        const success = await projectFilesStore.uploadFile(projectId, file);
        if (success) {
          successCount++;
        } else {
          failCount++;
        }
      }
      
      if (successCount > 0) {
        message.success(`${successCount} ${successCount === 1 ? t('FileUploaded') : t('FilesUploaded')}`);
        if (failCount > 0) {
          message.warning(`${failCount} ${failCount === 1 ? t('FileFailedToUpload') : t('FilesFailedToUpload')}`);
        }
        setUploadFileList([]);
        setIsUploadModalVisible(false);
      } else {
        message.error(projectFilesStore.error || t('FailedToUploadFiles'));
      }
    } catch (error) {
      message.error(t('FailedToUploadFiles'));
    }
  };

  const confirmDelete = (item: ProjectFile) => {
    if (!canManageFiles) return;

    setSelectedItem(item);
    setIsDeleteModalVisible(true);
  };

  const handleDelete = async () => {
    if (!selectedItem || !projectId) {
      message.error(t('NoItemSelected'));
      return;
    }
    
    try {
      const success = await projectFilesStore.deleteItem(projectId, selectedItem, false);
      
      if (success) {
        message.success(selectedItem.isFolder ? t('FolderDeleted') : t('FileDeleted'));
        setSelectedItem(null);
        setIsDeleteModalVisible(false);
      } else {
        message.error(projectFilesStore.error || (selectedItem.isFolder ? t('FailedToDeleteFolder') : t('FailedToDeleteFile')));
      }
    } catch (error) {
      message.error(selectedItem.isFolder ? t('FailedToDeleteFolder') : t('FailedToDeleteFile'));
    }
  };

  const handleRename = async () => {
    if (!canManageFiles) return;

    if (!selectedItem || !projectId || !newItemName.trim()) {
      message.error(t('PleaseEnterNewName'));
      return;
    }
    
    try {
      const success = await projectFilesStore.renameItem(projectId, selectedItem, newItemName);
      
      if (success) {
        message.success(selectedItem.isFolder ? t('FolderRenamed') : t('FileRenamed'));
        setSelectedItem(null);
        setNewItemName('');
        setIsRenameModalVisible(false);
      } else {
        message.error(projectFilesStore.error || t('FailedToRename'));
      }
    } catch (error) {
      message.error(t('FailedToRename'));
    }
  };

  const showRenameModal = (item: ProjectFile) => {
    if (!canManageFiles) return;

    setSelectedItem(item);
    setNewItemName(getItemName(item));
    setIsRenameModalVisible(true);
  };

  const handleDragStart = (e: React.DragEvent, item: ProjectFile) => {
    if (!canManageFiles) return;

    e.dataTransfer.effectAllowed = 'move';
    projectFilesStore.startDrag(item);
  };

  const handleDragOver = (e: React.DragEvent, item: ProjectFile) => {
    if (!canManageFiles) return;

    e.preventDefault();
    if (item.isFolder) {
      e.dataTransfer.dropEffect = 'move';
      setDraggedOverId(item.id);
    }
  };

  const handleDragLeave = () => {
    setDraggedOverId(null);
  };

  const handleDrop = async (e: React.DragEvent, targetItem: ProjectFile) => {
    e.preventDefault();
    setDraggedOverId(null);
    
    if (!projectId || !canManageFiles) return;
    
    const success = await projectFilesStore.handleDrop(projectId, targetItem);
    if (success) {
      message.success(t('ItemMoved'));
    } else if (projectFilesStore.error) {
      message.error(projectFilesStore.error);
    }
  };

  const renderBreadcrumbs = () => (
    <Breadcrumb style={{ margin: '16px 0' }}>
      {projectFilesStore.breadcrumbs.map((crumb, index) => (
        <Breadcrumb.Item
          key={`${crumb.folderId}-${index}`}
          onClick={() => handleBreadcrumbClick(index)}
        >
          {index === 0 ? <><HomeOutlined /> {crumb.name}</> : crumb.name}
        </Breadcrumb.Item>
      ))}
    </Breadcrumb>
  );

  const formatFileSize = (size?: number): string => {
    if (!size) return '—';
    
    const kb = size / 1024;
    if (kb < 1024) {
      return `${kb.toFixed(2)} KB`;
    }
    
    const mb = kb / 1024;
    if (mb < 1024) {
      return `${mb.toFixed(2)} MB`;
    }
    
    const gb = mb / 1024;
    return `${gb.toFixed(2)} GB`;
  };

  const formatDate = (date?: string): string => {
    if (!date) return '—';
    return dayjs(date).format('MMM D, YYYY h:mm A');
  };

  const canUploadFiles = canAccessProject(project, applicationStore.user, "write");
  const canManageFiles = canAccessProject(project, applicationStore.user, "full");

  const renderGridView = () => {
    const files = projectFilesStore.files || [];
    
    return (
      <Row gutter={[16, 16]}>
        {files.length === 0 ? (
          <Col span={24}>
            <Empty
              description={t('NoFilesOrFolders')}
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          </Col>
        ) : (
          files.map((item) => (
            <Col key={item.id} xs={12} sm={8} md={6} lg={4} xl={4}>
              <Card
                hoverable
                draggable={canManageFiles}
                onDragStart={(e) => handleDragStart(e, item)}
                onDragOver={(e) => handleDragOver(e, item)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, item)}
                style={{ 
                  cursor: item.isFolder ? 'pointer' : 'default',
                  backgroundColor: draggedOverId === item.id ? '#e6f7ff' : undefined
                }}
                bodyStyle={{ padding: '12px', textAlign: 'center' }}
                onClick={() => item.isFolder && handleFolderClick(item)}
              >
                <div style={{ height: '60px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  {item.isFolder ? (
                    <FolderOutlined style={{ fontSize: '40px', color: '#faad14' }} />
                  ) : (
                    <div style={{ fontSize: '40px', color: '#1890ff' }}>
                      {getFileIcon(item.fileName)}
                    </div>
                  )}
                </div>
                <Tooltip title={getItemName(item)}>
                  <div style={{ 
                    marginTop: '8px', 
                    overflow: 'hidden', 
                    textOverflow: 'ellipsis', 
                    whiteSpace: 'nowrap' 
                  }}>
                    {getItemName(item)}
                  </div>
                </Tooltip>
                <div style={{ marginTop: '8px' }}>
                  <Space>
                    {!item.isFolder && (
                      <Tooltip title={t('Download')}>
                        <Button 
                          type="text" 
                          icon={<DownloadOutlined />} 
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            window.open(item.url, '_blank');
                          }}
                        />
                      </Tooltip>
                    )}
                    {canManageFiles && (
                      <>
                        <Tooltip title={t('Rename')}>
                          <Button 
                            type="text" 
                            icon={<EditOutlined />} 
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              showRenameModal(item);
                            }}
                          />
                        </Tooltip>
                        <Tooltip title={t('Delete')}>
                          <Button 
                            type="text" 
                            danger 
                            icon={<DeleteOutlined />} 
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              confirmDelete(item);
                            }}
                          />
                        </Tooltip>
                      </>
                    )}
                  </Space>
                </div>
              </Card>
            </Col>
          ))
        )}
      </Row>
    );
  };

  const renderListView = () => {
    const columns = [
      {
        title: t('Name'),
        dataIndex: 'fileName',
        key: 'fileName',
        render: (text: string, record: ProjectFile) => (
          <div 
            style={{ 
              display: 'flex', 
              alignItems: 'center',
              cursor: record.isFolder ? 'pointer' : 'default'
            }}
            onClick={() => record.isFolder && handleFolderClick(record)}
            draggable={canManageFiles}
            onDragStart={(e) => handleDragStart(e, record)}
            onDragOver={(e) => handleDragOver(e, record)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, record)}
          >
            <DragOutlined style={{ marginRight: '8px', color: '#999' }} />
            {record.isFolder ? (
              <FolderOutlined style={{ marginRight: '8px', color: '#faad14', fontSize: '16px' }} />
            ) : (
              <span style={{ marginRight: '8px', color: '#1890ff', fontSize: '16px' }}>
                {getFileIcon(record.fileName)}
              </span>
            )}
            <span>{getItemName(record)}</span>
          </div>
        ),
      },
      {
        title: t('Type'),
        dataIndex: 'extension',
        key: 'extension',
        width: 120,
        render: (_: any, record: ProjectFile) => (
          record.isFolder ? t('Folder') : (record.extension || '—').toUpperCase()
        ),
      },
      {
        title: t('Size'),
        dataIndex: 'size',
        key: 'size',
        width: 120,
        render: (_: any, record: ProjectFile) => (
          record.isFolder ? '—' : formatFileSize(record.size)
        ),
      },
      {
        title: t('Modified'),
        dataIndex: 'lastModified',
        key: 'lastModified',
        width: 180,
        render: (date: string) => formatDate(date),
      },
      {
        title: t('Actions'),
        key: 'actions',
        width: 150,
        render: (_: any, record: ProjectFile) => (
          <Space>
            {!record.isFolder && (
              <Tooltip title={t('Download')}>
                <Button
                  type="text"
                  icon={<DownloadOutlined />}
                  size="small"
                  onClick={() => window.open(record.url, '_blank')}
                />
              </Tooltip>
            )}
            {canManageFiles && (
              <>
                <Tooltip title={t('Rename')}>
                  <Button
                    type="text"
                    icon={<EditOutlined />}
                    size="small"
                    onClick={() => showRenameModal(record)}
                  />
                </Tooltip>
                <Tooltip title={t('Delete')}>
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    size="small"
                    onClick={() => confirmDelete(record)}
                  />
                </Tooltip>
              </>
            )}
          </Space>
        ),
      },
    ];

    const files = projectFilesStore.files || [];

    return (
      <Table
        dataSource={files}
        columns={columns}
        rowKey="id"
        pagination={{ pageSize: 10 }}
        locale={{ emptyText: t('NoFilesOrFolders') }}
        rowClassName={(record) => draggedOverId === record.id ? 'dragged-over' : ''}
      />
    );
  };

  const isPageLoading = isProjectLoading || projectFilesStore.isLoading;

  return (
    <Content style={{ padding: '0 24px 24px', background: '#fff' }}>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        padding: '16px 0' 
      }}>
        <Title level={4}>
          {project?.title || t('Project')} - {t('Files')}
        </Title>
        <Space>
          {canUploadFiles && (
            <Button 
              type="primary" 
              icon={<UploadOutlined />}
              onClick={() => setIsUploadModalVisible(true)}
              disabled={!projectId}
            >
              {t('UploadFiles')}
            </Button>
          )}
          {canManageFiles && (
            <Button 
              icon={<PlusOutlined />}
              onClick={() => setIsCreateFolderModalVisible(true)}
              disabled={!projectId}
            >
              {t('NewFolder')}
            </Button>
          )}
          {projectFilesStore.breadcrumbs.length > 1 && (
            <Button 
              icon={<ArrowLeftOutlined />}
              onClick={handleGoToParent}
            >
              {t('Up')}
            </Button>
          )}
          <Dropdown
            menu={{
              items: [
                {
                  key: 'list',
                  label: t('ListView'),
                  onClick: () => setViewMode('list')
                },
                {
                  key: 'grid',
                  label: t('GridView'),
                  onClick: () => setViewMode('grid')
                }
              ]
            }}
          >
            <Button icon={<EllipsisOutlined />} />
          </Dropdown>
        </Space>
      </div>
      
      {renderBreadcrumbs()}
      
      <Divider style={{ margin: '12px 0' }} />
      
      {projectFilesStore.error && (
        <Alert
          message={t('Error')}
          description={projectFilesStore.error}
          type="error"
          showIcon
          closable
          onClose={() => projectFilesStore.setError(null)}
          style={{ marginBottom: '16px' }}
        />
      )}
      
      <Spin spinning={isShowInternalLoader && isPageLoading}>
        {viewMode === 'grid' ? renderGridView() : renderListView()}
      </Spin>
      
      <Modal
        title={t('CreateNewFolder')}
        open={isCreateFolderModalVisible}
        onCancel={() => {
          setIsCreateFolderModalVisible(false);
          setNewFolderName('');
        }}
        onOk={handleCreateFolder}
        okButtonProps={{ disabled: !newFolderName.trim() }}
      >
        <Form layout="vertical">
          <Form.Item 
            label={t('FolderName')}
            rules={[{ required: true, message: t('PleaseEnterFolderName') }]}
          >
            <Input
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              placeholder={t('EnterFolderName')}
              autoFocus
              maxLength={50}
            />
          </Form.Item>
        </Form>
      </Modal>
      
      <Modal
        title={t('UploadFiles')}
        open={isUploadModalVisible}
        onCancel={() => {
          setIsUploadModalVisible(false);
          setUploadFileList([]);
        }}
        onOk={handleUpload}
        okButtonProps={{ disabled: uploadFileList.length === 0 }}
        width={600}
      >
        <Dragger
          multiple
          beforeUpload={(file) => {
            const isLt50M = file.size / 1024 / 1024 < 50;
            if (!isLt50M) {
              message.error(t('FileTooLarge'));
              return Upload.LIST_IGNORE;
            }
            return false;
          }}
          fileList={uploadFileList}
          onChange={({ fileList }) => setUploadFileList(fileList)}
          onRemove={(file) => {
            setUploadFileList((prev) => 
              prev.filter((item) => item.uid !== file.uid)
            );
          }}
        >
          <p className="ant-upload-drag-icon">
            <UploadOutlined style={{ fontSize: '32px', color: '#1890ff' }} />
          </p>
          <p className="ant-upload-text">{t('DragOrClickToUpload')}</p>
          <p className="ant-upload-hint">{t('UploadHint')}</p>
        </Dragger>
      </Modal>
      
      <Modal
        title={selectedItem?.isFolder ? t('DeleteFolder') : t('DeleteFile')}
        open={isDeleteModalVisible}
        onCancel={() => {
          setIsDeleteModalVisible(false);
          setSelectedItem(null);
        }}
        onOk={handleDelete}
        okType="danger"
        okText={t('Delete')}
      >
        {selectedItem && (
          <p>
            {selectedItem.isFolder
              ? t('DeleteFolderConfirmation', { name: getItemName(selectedItem) })
              : t('DeleteFileConfirmation', { name: getItemName(selectedItem) })}
          </p>
        )}
        {selectedItem?.isFolder && (
          <Alert
            message={t('Warning')}
            description={t('DeleteFolderWarning')}
            type="warning"
            showIcon
            style={{ marginTop: '16px' }}
          />
        )}
      </Modal>
      
      <Modal
        title={selectedItem?.isFolder ? t('RenameFolder') : t('RenameFile')}
        open={isRenameModalVisible}
        onCancel={() => {
          setIsRenameModalVisible(false);
          setSelectedItem(null);
          setNewItemName('');
        }}
        onOk={handleRename}
        okButtonProps={{ disabled: !newItemName.trim() }}
      >
        <Form layout="vertical">
          <Form.Item 
            label={t('NewName')}
            rules={[{ required: true, message: t('PleaseEnterNewName') }]}
          >
            <Input
              value={newItemName}
              onChange={(e) => setNewItemName(e.target.value)}
              placeholder={t('EnterNewName')}
              autoFocus
              maxLength={50}
            />
          </Form.Item>
        </Form>
      </Modal>
    </Content>
  );
});

export default ProjectFiles;
